import { useCallback, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { RegisterLayout, RegisterLayouts } from '@floorfile/contracts'
import type { RegisterWidthsBinding, UseRegisterWidths } from '@floorfile/ui'
import { useAuth } from './auth'
import { parseApiResponse } from './api-response'
import { readUserJson, removeUserValue, useStorageScope, writeUserJson, type StorageScope } from './user-storage'

/**
 * F27 — each register's personal layout (column widths + visible columns),
 * kept on the SERVER per user so it follows them to another device and
 * survives sign-out, with a write-through copy in user-scoped localStorage
 * for an instant first paint and for offline.
 *
 * Writes are one register at a time (`PUT /settings/profile/layouts/{key}`),
 * debounced 500 ms, so a drag or a run of arrow presses is one request. A
 * write not yet acknowledged is remembered as "unsent" in localStorage: after
 * a reload inside the debounce window (or offline) the local layout wins over
 * the server's older copy and is sent again, instead of being overwritten.
 */
const CACHE_NAME = 'register-layouts'
const UNSENT_NAME = 'register-layouts.unsent'
const FLUSH_MS = 500

export const registerLayoutsKey = (scope: StorageScope | undefined) =>
  ['register-layouts', scope?.companyId ?? '', scope?.userId ?? ''] as const

type AuthFetch = ReturnType<typeof useAuth>['authFetch']

const timers = new Map<string, ReturnType<typeof setTimeout>>()

function unsent(scope: StorageScope): string[] {
  const list = readUserJson<string[] | null>(scope, UNSENT_NAME, null)
  return Array.isArray(list) ? list : []
}
function markUnsent(scope: StorageScope, layoutKey: string, on: boolean) {
  const list = unsent(scope).filter((key) => key !== layoutKey)
  writeUserJson(scope, UNSENT_NAME, on ? [...list, layoutKey] : list)
}

function isEmpty(layout: RegisterLayout | undefined): boolean {
  return !layout || (!layout.widths && !layout.visible && !layout.knownKeys)
}

function scheduleFlush(queryClient: QueryClient, scope: StorageScope, layoutKey: string, authFetch: AuthFetch) {
  const id = `${scope.companyId}:${scope.userId}:${layoutKey}`
  const existing = timers.get(id)
  if (existing) clearTimeout(existing)
  timers.set(
    id,
    setTimeout(() => {
      timers.delete(id)
      const layout = queryClient.getQueryData<RegisterLayouts>(registerLayoutsKey(scope))?.[layoutKey]
      const path = `/api/v1/settings/profile/layouts/${encodeURIComponent(layoutKey)}`
      const request = isEmpty(layout)
        ? authFetch(path, { method: 'DELETE' })
        : authFetch(path, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(layout) })
      void request
        .then((response) => {
          // Sent (or refused for good, e.g. the 200-register cap): either way,
          // retrying the same body will not help. The layout stays local.
          if (response.ok || response.status === 422) markUnsent(scope, layoutKey, false)
        })
        // Offline / network: stays unsent, re-sent on the next load or change.
        .catch(() => undefined)
    }, FLUSH_MS),
  )
}

function useLayoutsQuery() {
  const { authFetch } = useAuth()
  const scope = useStorageScope()
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: registerLayoutsKey(scope),
    enabled: scope !== undefined,
    queryFn: async () => {
      const body = await parseApiResponse<{ data: RegisterLayouts }>(await authFetch('/api/v1/settings/profile/layouts'))
      if (!scope) return body.data
      // A local change the server has not acknowledged wins, and goes again.
      const local = queryClient.getQueryData<RegisterLayouts>(registerLayoutsKey(scope)) ?? readUserJson<RegisterLayouts>(scope, CACHE_NAME, {})
      const pendingKeys = unsent(scope)
      const merged: RegisterLayouts = Object.fromEntries(Object.entries(body.data).filter(([key]) => !pendingKeys.includes(key)))
      for (const key of pendingKeys) {
        const mine = local[key]
        if (mine) merged[key] = mine
        scheduleFlush(queryClient, scope, key, authFetch)
      }
      writeUserJson(scope, CACHE_NAME, merged)
      return merged
    },
    // First paint from the local copy; the server answer replaces it at once.
    initialData: () => (scope ? readUserJson<RegisterLayouts | undefined>(scope, CACHE_NAME, undefined) : undefined),
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60_000,
  })
}

/** One register's saved layout and a setter that saves it (locally at once, to the server shortly after). */
export function useRegisterLayout(layoutKey: string | undefined) {
  const query = useLayoutsQuery()
  const queryClient = useQueryClient()
  const scope = useStorageScope()
  const { authFetch } = useAuth()
  const layout = layoutKey ? query.data?.[layoutKey] : undefined
  const update = useCallback(
    (change: (previous: RegisterLayout | undefined) => RegisterLayout | undefined) => {
      if (!layoutKey || !scope) return
      const key = registerLayoutsKey(scope)
      const all = queryClient.getQueryData<RegisterLayouts>(key) ?? {}
      const next = change(all[layoutKey])
      const others = Object.fromEntries(Object.entries(all).filter(([other]) => other !== layoutKey))
      const updated: RegisterLayouts = next && !isEmpty(next) ? { ...others, [layoutKey]: next } : others
      queryClient.setQueryData(key, updated)
      writeUserJson(scope, CACHE_NAME, updated)
      markUnsent(scope, layoutKey, true)
      scheduleFlush(queryClient, scope, layoutKey, authFetch)
    },
    [layoutKey, scope, queryClient, authFetch],
  )
  return { layout, update, loaded: query.isFetched }
}

function withoutUndefined(layout: RegisterLayout): RegisterLayout | undefined {
  const entries = Object.entries(layout).filter(([, value]) => value !== undefined)
  return entries.length ? (Object.fromEntries(entries) as RegisterLayout) : undefined
}

/** `DataTable`'s width binding (through `RegisterLayoutProvider`, mounted once in `providers.tsx`). */
export const useRegisterWidthsBinding: UseRegisterWidths = (layoutKey): RegisterWidthsBinding => {
  const { layout, update } = useRegisterLayout(layoutKey)
  return {
    widths: layout?.widths,
    setWidths: (widths) => update((previous) => withoutUndefined({ ...previous, widths: widths ? { ...widths } : undefined })),
  }
}

/**
 * The visible-column rule, pure (tested in `register-layout.test.ts`):
 * nothing saved → the defaults; a saved key the register no longer has →
 * dropped; a default column the register did not have when the list was
 * saved (not in `knownKeys`) → shown; a list saved before `knownKeys` existed
 * → exactly as saved.
 */
export function visibleFromLayout(
  layout: Pick<RegisterLayout, 'visible' | 'knownKeys'> | undefined,
  all: readonly string[],
  defaults: readonly string[],
): string[] {
  if (!layout?.visible) return [...defaults]
  const saved = layout.visible
  const known = layout.knownKeys
  const fresh = known ? defaults.filter((key) => !known.includes(key) && !saved.includes(key)) : []
  return [...saved.filter((key) => all.includes(key)), ...fresh]
}

/**
 * A register's visible columns, from the same per-user layout.
 *
 * `undefined` = the register's defaults — the chooser's Reset and a saved view
 * without a column list land here, so a later change to the default set
 * reaches users who never customised.
 *
 * A column the register did not have when the list was saved (`knownKeys`) is
 * SHOWN if it is shown by default — a new facility's Stock column (F28) is not
 * a column the user hid. A saved key the register no longer has is dropped.
 *
 * Migrates the pre-F27 list (`columns.<name>` in localStorage) to the server
 * the first time the register loads for this user.
 */
export function useVisibleColumns(
  name: string,
  columns: readonly { key: string; defaultHidden?: boolean | undefined }[],
): [string[], (keys: string[] | undefined) => void] {
  const { layout, update, loaded } = useRegisterLayout(name)
  const scope = useStorageScope()
  const allKeys = columns.map((column) => column.key)
  const allSignature = allKeys.join('|')
  const defaultSignature = columns
    .filter((column) => !column.defaultHidden)
    .map((column) => column.key)
    .join('|')
  const visible = useMemo(
    () =>
      visibleFromLayout(
        layout,
        allSignature ? allSignature.split('|') : [],
        defaultSignature ? defaultSignature.split('|') : [],
      ),
    [layout, allSignature, defaultSignature],
  )
  const set = useCallback(
    (keys: string[] | undefined) =>
      update((previous) =>
        withoutUndefined({
          ...previous,
          visible: keys,
          knownKeys: keys ? (allSignature ? allSignature.split('|') : []) : undefined,
        }),
      ),
    [update, allSignature],
  )
  useEffect(() => {
    if (!loaded || !scope) return
    const legacyName = `columns.${name}`
    const legacy = readUserJson<string[] | null>(scope, legacyName, null)
    if (legacy === null) return
    if (Array.isArray(legacy) && !layout?.visible) set(legacy)
    removeUserValue(scope, legacyName)
  }, [loaded, scope, name, layout?.visible, set])
  return [visible, set]
}
